import { useState } from "react";
import type { AccessibilityActionEvent } from "react-native";

import type { Point } from "@heraion/shared";

import { plotKeyboardProps } from "./plotKeyboard";

// How every chart is read, bars and lines: the host's header quotes one point,
// «value · date», the latest by default; a tap picks another (PG-63, PG-64).

/** Which point the header reads, by index into the points the host passed.
 *  A second pick of the same point goes back to the latest. */
export function usePointSelection(count: number) {
  const [picked, setPicked] = useState<number | null>(null);
  const last = count - 1;
  const selected = picked !== null && picked <= last ? picked : last;
  const select = (i: number) => setPicked(i === selected ? null : i);
  return { selected, select, count };
}

export type PointSelection = ReturnType<typeof usePointSelection> & {
  /** The header's text for the selected point, when the host built one. */
  readout?: string;
};

/** On a bar chart's header: a new pick is announced, value and date in one
 *  breath. RNW drops `accessibilityLiveRegion`, the `aria-*` reach the DOM. */
const READOUT_LIVE = {
  accessibilityLiveRegion: "polite",
  "aria-live": "polite",
  "aria-atomic": true,
} as const;

/** The selection over `points`, the header's text for it, and the props for
 *  the header's Text. `readout` is undefined only when there is no point.
 *  `adjustable`: a line's plot is a slider that already speaks the readout
 *  (`plotA11yProps`), so only the bars' header is a live region. */
export function useChartReadout(
  points: ReadonlyArray<Point>,
  format: (y: number) => string,
  adjustable: boolean,
) {
  const base = usePointSelection(points.length);
  const picked = points[base.selected];
  const readout = picked ? `${format(picked.y)} · ${picked.label}` : undefined;
  const selection: PointSelection = { ...base, readout };
  return { selection, readout, readoutProps: adjustable ? {} : READOUT_LIVE };
}

/** A plot to a screen reader: an image named by `label`, or, with a
 *  selection, an adjustable (a slider on web, arrow keys) that moves the same
 *  selection a tap moves, its value the header's readout. */
export function plotA11yProps(label: string, selection?: PointSelection) {
  if (!selection) {
    return { accessible: true, accessibilityRole: "image" as const, accessibilityLabel: label };
  }
  const step = (d: 1 | -1) => {
    const next = selection.selected + d;
    if (next >= 0 && next < selection.count) selection.select(next);
  };
  return {
    accessible: true,
    accessibilityRole: "adjustable" as const,
    accessibilityLabel: label,
    "aria-valuemin": 1,
    "aria-valuemax": selection.count,
    "aria-valuenow": selection.selected + 1,
    "aria-valuetext": selection.readout,
    accessibilityActions: [{ name: "increment" }, { name: "decrement" }],
    onAccessibilityAction: (e: AccessibilityActionEvent) => {
      if (e.nativeEvent.actionName === "increment") step(1);
      else if (e.nativeEvent.actionName === "decrement") step(-1);
    },
    ...plotKeyboardProps(step),
  };
}
